(() => {
  const selector = '[data-cv-e8e00129-controller]';
  const controllers = new WeakMap();

  class CVE8E00129Purchase {
    constructor(root) {
      this.root = root;
      this.abort = new AbortController();
      this.variants = this.readJson('[data-cv-e8e00129-variants]');
      this.gallery = this.readJson('[data-cv-e8e00129-gallery]');
      this.optionGroups = [...root.querySelectorAll('[data-cv-e8e00129-option]')];
      this.form = root.querySelector('[data-cv-e8e00129-product-form]');
      this.variantInput = root.querySelector('[data-cv-e8e00129-variant-input]');
      this.quantityInput = root.querySelector('[data-cv-e8e00129-quantity]');
      this.heroImage = root.querySelector('[data-cv-e8e00129-hero-image]');
      this.mobileSource = root.querySelector('[data-cv-e8e00129-mobile-source]');
      this.addButton = root.querySelector('[data-cv-e8e00129-add]');
      this.dialog = root.querySelector('[data-cv-e8e00129-dialog]');
      this.dialogImage = root.querySelector('[data-cv-e8e00129-gallery-image]');
      this.dialogCaption = root.querySelector('[data-cv-e8e00129-gallery-caption]');
      this.liveRegion = root.querySelector('[data-cv-e8e00129-live]');
      this.returnFocus = null;
      this.galleryIndex = 0;

      if (!this.form || !this.variantInput || !this.variants.length) return;
      this.bind();
      this.bindLoadingState();
      this.restoreFromUrl(false);
    }

    readJson(query) {
      const node = this.root.querySelector(query);
      if (!node) return [];
      try {
        return JSON.parse(node.textContent || '[]');
      } catch (_) {
        return [];
      }
    }

    bind() {
      const signal = this.abort.signal;

      this.root.addEventListener('change', (event) => {
        if (event.target.matches('[data-cv-e8e00129-option] input')) {
          this.update(this.findByOptions(), { historyMode: 'push', announce: true });
        }
        if (event.target.matches('[data-cv-e8e00129-quantity]')) this.normalizeQuantity();
      }, { signal });

      this.root.addEventListener('keydown', (event) => {
        const activationKeys = ['Enter', ' ', 'Spacebar'];
        if (!activationKeys.includes(event.key)) return;

        const addButton = event.target.closest('[data-cv-e8e00129-add]');
        if (addButton && this.form) {
          event.preventDefault();
          event.stopPropagation();
          this.submitNativeFallback(this.form, addButton);
          return;
        }

        if (event.target.closest('[data-cv-e8e00129-quantity-minus]')) {
          event.preventDefault();
          this.stepQuantity(-1);
          return;
        }

        if (event.target.closest('[data-cv-e8e00129-quantity-plus]')) {
          event.preventDefault();
          this.stepQuantity(1);
          return;
        }

        const galleryTrigger = event.target.closest('[data-cv-e8e00129-gallery-open]');
        if (!galleryTrigger) return;

        event.preventDefault();
        this.openGallery(galleryTrigger);
      }, { signal, capture: true });

      this.root.addEventListener('click', (event) => {
        if (event.target.closest('[data-cv-e8e00129-quantity-minus]')) {
          event.preventDefault();
          this.stepQuantity(-1);
          return;
        }
        if (event.target.closest('[data-cv-e8e00129-quantity-plus]')) {
          event.preventDefault();
          this.stepQuantity(1);
          return;
        }
        const galleryTrigger = event.target.closest('[data-cv-e8e00129-gallery-open]');
        if (galleryTrigger) {
          event.preventDefault();
          this.openGallery(galleryTrigger);
          return;
        }
        if (event.target.closest('[data-cv-e8e00129-gallery-close]')) this.closeGallery();
        if (event.target.closest('[data-cv-e8e00129-gallery-previous]')) this.showGallery(this.galleryIndex - 1);
        if (event.target.closest('[data-cv-e8e00129-gallery-next]')) this.showGallery(this.galleryIndex + 1);
      }, { signal });

      window.addEventListener('popstate', () => this.restoreFromUrl(false), { signal });

      if (this.dialog) {
        this.dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.closeGallery();
        }, { signal });

        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.closeGallery();
        }, { signal });

        this.dialog.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            this.closeGallery();
            return;
          }

          if (['Enter', ' ', 'Spacebar'].includes(event.key)) {
            if (event.target.closest('[data-cv-e8e00129-gallery-close]')) {
              event.preventDefault();
              this.closeGallery();
              return;
            }
            if (event.target.closest('[data-cv-e8e00129-gallery-previous]')) {
              event.preventDefault();
              this.showGallery(this.galleryIndex - 1);
              return;
            }
            if (event.target.closest('[data-cv-e8e00129-gallery-next]')) {
              event.preventDefault();
              this.showGallery(this.galleryIndex + 1);
              return;
            }
          }

          if (event.key === 'ArrowLeft') this.showGallery(this.galleryIndex - 1);
          if (event.key === 'ArrowRight') this.showGallery(this.galleryIndex + 1);
          if (event.key === 'Tab') this.trapFocus(event);
        }, { signal });

        this.dialog.addEventListener('close', () => {
          this.dialogImage?.removeAttribute('src');
          document.documentElement.classList.remove('cv-e8e00129-dialog-open');
          this.returnFocus?.focus();
          this.returnFocus = null;
        }, { signal });
      }
    }

    bindLoadingState() {
      if (!this.addButton) return;
      const sync = () => {
        this.addButton = this.root.querySelector('[data-cv-e8e00129-add]');
        if (!this.addButton) return;

        const busy = this.addButton.hasAttribute('data-added');
        this.addButton.setAttribute('aria-busy', busy ? 'true' : 'false');
        this.addButton.disabled = !this.currentVariant?.available;

        clearTimeout(this.loadingFallback);
        if (busy) this.loadingFallback = setTimeout(() => this.settleLoadingState(), 3000);
      };

      this.loadingObserver = new MutationObserver(sync);
      this.loadingObserver.observe(this.root, {
        attributes: true,
        attributeFilter: ['data-added'],
        childList: true,
        subtree: true,
      });

      const errorRegion = this.root.querySelector('[ref="addToCartTextError"]');
      if (errorRegion) {
        this.errorObserver = new MutationObserver(() => {
          if (errorRegion.textContent.trim()) this.settleLoadingState();
        });
        this.errorObserver.observe(errorRegion, { childList: true, characterData: true, subtree: true });
      }

      sync();
    }

    settleLoadingState() {
      clearTimeout(this.loadingFallback);
      this.addButton = this.root.querySelector('[data-cv-e8e00129-add]');
      if (!this.addButton) return;

      this.addButton.removeAttribute('data-added');
      this.addButton.classList.remove('loading');
      this.addButton.setAttribute('aria-busy', 'false');
      this.addButton.disabled = !this.currentVariant?.available;
    }

    async submitNativeFallback(form, submitter) {
      if (form.dataset.cvE8E00129FallbackSubmitting === 'true') return;

      form.dataset.cvE8E00129FallbackSubmitting = 'true';
      if (submitter) {
        submitter.disabled = true;
        submitter.setAttribute('aria-busy', 'true');
      }

      try {
        const response = await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
          credentials: 'same-origin',
        });
        const payload = await response.json();

        if (!response.ok || payload.status) {
          throw new Error(payload.description || payload.message || 'Unable to add this item to the cart.');
        }

        window.location.assign(Theme.routes.cart_url || '/cart');
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unable to add this item to the cart.';
        const errorRegion = this.root.querySelector('[ref="addToCartTextError"]');
        if (errorRegion) errorRegion.textContent = message;
        if (this.liveRegion) this.liveRegion.textContent = message;
        form.removeAttribute('data-cv-e8e00129-fallback-submitting');
        if (submitter) {
          submitter.removeAttribute('aria-busy');
          submitter.disabled = !this.currentVariant?.available;
        }
      }
    }

    selectedOptions() {
      return this.optionGroups.map((group) => group.querySelector('input:checked')?.value || '');
    }

    findByOptions() {
      const options = this.selectedOptions();
      return this.variants.find((variant) => variant.options.every((value, index) => value === options[index]));
    }

    findById(id) {
      return this.variants.find((variant) => String(variant.id) === String(id));
    }

    restoreFromUrl(announce) {
      const id = new URL(window.location.href).searchParams.get('variant') || this.variantInput.value;
      const variant = this.findById(id) || this.findByOptions() || this.variants[0];
      this.checkOptions(variant);
      this.update(variant, { historyMode: 'replace', announce });
    }

    checkOptions(variant) {
      if (!variant) return;
      this.optionGroups.forEach((group, index) => {
        const input = [...group.querySelectorAll('input')].find((node) => node.value === variant.options[index]);
        if (input) input.checked = true;
      });
    }

    update(variant, { historyMode = 'replace', announce = false } = {}) {
      if (!variant) return;
      this.currentVariant = variant;

      this.variantInput.value = variant.id;
      this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));

      this.optionGroups.forEach((group) => {
        const output = group.querySelector('[data-cv-e8e00129-option-value]');
        const checked = group.querySelector('input:checked');
        if (output && checked) output.textContent = checked.value;
      });

      this.setText('[data-cv-e8e00129-price]', variant.priceFormatted);
      this.setText('[data-cv-e8e00129-sku]', variant.sku || '—');

      const compare = this.root.querySelector('[data-cv-e8e00129-compare]');
      if (compare) {
        compare.hidden = !(variant.compareAt > variant.price);
        compare.textContent = variant.compareAt > variant.price ? variant.compareAtFormatted : '';
      }

      const addButton = this.root.querySelector('[data-cv-e8e00129-add]');
      if (addButton) {
        addButton.disabled = !variant.available;
        addButton.setAttribute('aria-disabled', variant.available ? 'false' : 'true');
        const label = addButton.querySelector('[data-cv-e8e00129-add-label]');
        if (label) label.textContent = variant.available ? addButton.dataset.readyLabel : addButton.dataset.unavailableLabel;
      }

      this.updateQuantityRules(variant);
      this.updateHero(variant);

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      const state = { variantId: variant.id };
      if (historyMode === 'push') window.history.pushState(state, '', url);
      else window.history.replaceState(state, '', url);

      if (announce && this.liveRegion) {
        this.liveRegion.textContent = `${variant.title}. ${variant.priceFormatted}. ${variant.available ? 'Available' : 'Unavailable'}.`;
      }

      window.dispatchEvent(new CustomEvent('cv-e8e00129:variant-change', {
        detail: { productId: this.root.dataset.productId, variant },
      }));
    }

    updateQuantityRules(variant) {
      if (!this.quantityInput) return;
      const minimum = Number(variant.quantityMin) || 1;
      const increment = Number(variant.quantityIncrement) || 1;
      this.quantityInput.min = String(minimum);
      this.quantityInput.step = String(increment);
      if (variant.quantityMax == null) this.quantityInput.removeAttribute('max');
      else this.quantityInput.max = String(variant.quantityMax);
      this.normalizeQuantity();
    }

    normalizeQuantity() {
      if (!this.quantityInput) return;
      const minimum = Number(this.quantityInput.min) || 1;
      const maximum = this.quantityInput.max ? Number(this.quantityInput.max) : Infinity;
      const increment = Number(this.quantityInput.step) || 1;
      const raw = Number.parseInt(this.quantityInput.value, 10);
      const safe = Number.isFinite(raw) ? raw : minimum;
      const stepped = minimum + Math.round((safe - minimum) / increment) * increment;
      this.quantityInput.value = String(Math.min(maximum, Math.max(minimum, stepped)));
      const minus = this.root.querySelector('[data-cv-e8e00129-quantity-minus]');
      const plus = this.root.querySelector('[data-cv-e8e00129-quantity-plus]');
      if (minus) minus.disabled = Number(this.quantityInput.value) <= minimum;
      if (plus) plus.disabled = Number(this.quantityInput.value) >= maximum;
    }

    stepQuantity(direction) {
      if (!this.quantityInput) return;
      const increment = Number(this.quantityInput.step) || 1;
      const current = Number.parseInt(this.quantityInput.value, 10) || Number(this.quantityInput.min) || 1;
      this.quantityInput.value = String(current + direction * increment);
      this.normalizeQuantity();
      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    updateHero(variant) {
      if (!this.heroImage || !variant.image) return;
      this.heroImage.src = variant.image;
      if (variant.srcset) this.heroImage.srcset = variant.srcset;
      else this.heroImage.removeAttribute('srcset');
      this.heroImage.alt = variant.alt || '';
      if (this.mobileSource && !this.mobileSource.srcset) this.mobileSource.removeAttribute('srcset');
    }

    setText(selector, value) {
      const node = this.root.querySelector(selector);
      if (node) node.textContent = value ?? '';
    }

    openGallery(trigger) {
      if (!this.dialog || !this.gallery.length) return;
      this.returnFocus = trigger;
      this.showGallery(0);
      this.dialog.showModal();
      document.documentElement.classList.add('cv-e8e00129-dialog-open');
      this.dialog.querySelector('[data-cv-e8e00129-gallery-close]')?.focus();
    }

    showGallery(index) {
      if (!this.gallery.length || !this.dialogImage) return;
      this.galleryIndex = (index + this.gallery.length) % this.gallery.length;
      const item = this.gallery[this.galleryIndex];
      this.dialogImage.src = item.src;
      this.dialogImage.alt = item.alt || '';
      if (this.dialogCaption) {
        this.dialogCaption.textContent = item.caption || '';
        this.dialogCaption.hidden = !item.caption;
      }
      const position = this.dialog.querySelector('[data-cv-e8e00129-gallery-position]');
      if (position) position.textContent = `${this.galleryIndex + 1} / ${this.gallery.length}`;
    }

    trapFocus(event) {
      const focusable = [...this.dialog.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter((node) => !node.hidden && node.getClientRects().length);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    closeGallery() {
      if (this.dialog?.open) this.dialog.close();
    }

    destroy() {
      this.abort.abort();
      this.loadingObserver?.disconnect();
      this.errorObserver?.disconnect();
      clearTimeout(this.loadingFallback);
    }
  }

  const initialize = (scope = document) => {
    const roots = scope.matches?.(selector) ? [scope] : [...scope.querySelectorAll(selector)];
    roots.forEach((root) => {
      if (controllers.has(root)) return;
      controllers.set(root, new CVE8E00129Purchase(root));
    });
  };

  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.(selector) ? event.target : event.target.querySelector?.(selector);
    if (!root) return;
    controllers.get(root)?.destroy();
    controllers.delete(root);
  });
})();
